import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listNotifications,
  createNotification,
  updateNotification,
  deleteNotification,
  type Notification,
} from "../api/notifications";

const LEVELS = ["info", "warning", "critical"];

function levelBadgeClass(level: string): string {
  switch (level) {
    case "critical":
      return "bg-red-100 text-red-700";
    case "warning":
      return "bg-yellow-100 text-yellow-700";
    default:
      return "bg-blue-100 text-blue-700";
  }
}

export function NotificationsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["notifications"], queryFn: () => listNotifications() });
  const [editing, setEditing] = useState<Notification | "new" | null>(null);

  const remove = useMutation({
    mutationFn: (id: number) => deleteNotification(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  if (isLoading) return <p className="text-gray-500">Loading notifications…</p>;
  const rows = data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Notifications</h1>
        <button
          onClick={() => setEditing("new")}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
        >
          New notification
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Title</th>
              <th className="px-4 py-2">Level</th>
              <th className="px-4 py-2">Active</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((n) => (
              <tr key={n.notification_id}>
                <td className="px-4 py-2 font-medium">{n.title}</td>
                <td className="px-4 py-2">
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-xs font-medium capitalize ${levelBadgeClass(
                      n.level,
                    )}`}
                  >
                    {n.level}
                  </span>
                </td>
                <td className="px-4 py-2">{n.is_active ? "✓" : ""}</td>
                <td className="px-4 py-2 text-right space-x-3">
                  <button onClick={() => setEditing(n)} className="text-brand hover:underline">
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete notification "${n.title}"?`)) remove.mutate(n.notification_id);
                    }}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="text-gray-400 text-sm p-4">No notifications defined.</p>}
      </div>

      {editing && (
        <Editor
          notification={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["notifications"] });
          }}
        />
      )}
    </div>
  );
}

function Editor({
  notification,
  onClose,
  onSaved,
}: {
  notification: Notification | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(notification?.title ?? "");
  const [body, setBody] = useState(notification?.body ?? "");
  const [level, setLevel] = useState(notification?.level ?? "info");
  const [isActive, setIsActive] = useState(notification?.is_active ?? true);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        title,
        body,
        level,
        is_active: isActive,
      };
      return notification
        ? updateNotification(notification.notification_id, payload)
        : createNotification(payload).then(() => undefined);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg space-y-4">
        <h2 className="text-lg font-bold">{notification ? "Edit" : "New"} notification</h2>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Title</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Body</label>
          <textarea
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Level</label>
          <select
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm capitalize"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
          >
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active
        </label>
        <div className="flex gap-2 pt-2">
          <button
            disabled={!title || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
